import React from "react";
import { Modal } from "../Modal/Modal";
import { MODAL_SIZE } from "@constants/appDefaults";
import "./WizardModal.css";

/**
 * WizardModal Component
 * 
 * A modern wizard-based modal with step indicators and navigation.
 * Designed for multi-step forms with visual progress indication.
 * 
 * @param {Object} props
 * @param {string} props.title - Modal title
 * @param {Function} props.onClose - Close handler
 * @param {ReactNode} props.children - Modal content (wizard steps)
 * @param {number} props.currentStep - Current step number (1-based)
 * @param {number} props.totalSteps - Total number of steps
 * @param {Array} props.stepLabels - Optional array of step labels
 * @param {Object} props.customStyles - Custom styles
 * @param {boolean} props.disableBackdropClose - Prevent closing on backdrop click
 * @param {string} props.size - Modal size
 */
export const WizardModal = ({
  title,
  onClose,
  children,
  currentStep = 1,
  totalSteps = 1,
  stepLabels = [],
  customStyles = {},
  disableBackdropClose = false,
  size = MODAL_SIZE.MEDIUM,
  description,
  zIndex,
  isTop = true,
}) => {
  // Step Indicator Component
  const stepIndicator = totalSteps > 1 ? (
    <div className="wizard-step-indicator">
      <div className="wizard-steps-container">
        {Array.from({ length: totalSteps }, (_, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = stepNumber < currentStep;
          const stepLabel = stepLabels[index] || `Step ${stepNumber}`;

          return (
            <div
              key={stepNumber}
              className={`wizard-step ${isActive ? "wizard-step-active" : ""} ${
                isCompleted ? "wizard-step-completed" : ""
              } ${stepNumber > currentStep ? "wizard-step-pending" : ""}`}
            >
              <div className="wizard-step-circle">
                {isCompleted ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.3333 4L6 11.3333L2.66667 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span className="wizard-step-number">{stepNumber}</span>
                )}
              </div>
              <span className="wizard-step-label">{stepLabel}</span>
              {index < totalSteps - 1 && (
                <div
                  className={`wizard-step-connector ${
                    isCompleted ? "wizard-step-connector-completed" : ""
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <Modal
      title={title}
      onClose={onClose}
      customStyles={customStyles}
      disableBackdropClose={disableBackdropClose}
      size={size}
      description={description}
      headerContent={stepIndicator}
      zIndex={zIndex}
      isTop={isTop}
    >
      {/* Wizard Content */}
      <div className="wizard-content">{children}</div>
    </Modal>
  );
};

export default WizardModal;
